import React from 'react';
import { motion } from 'framer-motion';

/**
 * Composant Hero réutilisable pour les pages du site
 * @param {Object} props
 * @param {string} props.backgroundImage - URL de l'image de fond
 * @param {string} props.title - Titre principal
 * @param {string} props.subtitle - Sous-titre (optionnel)
 * @param {string} props.overlayColor - Couleur de l'overlay (optionnel, par défaut socabeg-blue)
 * @param {string} props.textPosition - Position du texte (optionnel, 'left', 'center', 'right')
 */
const PageHero = ({
  backgroundImage,
  title,
  subtitle,
  overlayColor = 'socabeg-blue',
  textPosition = 'left'
}) => {
  // Définir la classe de position du texte
  const textPositionClass = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end'
  }[textPosition];

  return (
    <section className="relative bg-white overflow-hidden min-h-[60vh] md:h-[80vh] lg:h-screen max-h-[900px]">
      {/* Image de fond avec effet de parallaxe */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-[1.05]"
        style={{ backgroundImage: `url(${backgroundImage})` }}
        initial={{ scale: 1.1 }}
        animate={{ scale: 1.05, x: '-2%' }}
        transition={{ duration: 8, ease: "easeInOut" }}
      >
        <img 
          src={backgroundImage} 
          alt="" 
          className="w-full h-full object-cover hidden" 
          aria-hidden="true"
        />
      </motion.div>
      
      {/* Overlay - dégradé moderne avec motif */}
      <div className={`absolute inset-0 bg-gradient-to-br from-${overlayColor}/90 via-${overlayColor}/70 to-socabeg-red/50 z-10`}>
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.2)_1px,transparent_1px)] bg-[length:24px_24px]"></div>
        <div className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-${overlayColor}/80 to-transparent`}></div>
      </div>
      
      {/* Contenu du hero avec animation */}
      <div className="container mx-auto px-4 h-full flex flex-col justify-center relative z-10 text-white">
        <motion.div
          className={`max-w-6xl flex flex-col ${textPositionClass}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Élément décoratif */}
          <motion.div 
            className="h-1 bg-socabeg-red mb-6 mt-8 md:mt-0 block"
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          ></motion.div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight tracking-tight">
            <motion.span 
              className="block max-w-5xl whitespace-pre-line"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {title}
            </motion.span>
          </h1>
          
          {subtitle && (
            <motion.p 
              className="text-xl md:text-2xl mb-10 text-white/90 font-light"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {subtitle}
            </motion.p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default PageHero;
